import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileSignature } from "lucide-react";
import {
  classKey,
  classLabelFromKey,
  getConsentFormForClass,
  getSessionUser,
  getStudentRecordForSession,
  getStudentsForClass,
  saveConsentFormForClass,
  saveConsentSubmissionForClass,
  saveStudentRecord,
} from "@/lib/eisStore";
import {
  decodeClassKey,
  isSupabaseConfigured,
  getLatestDocumentForClass,
  uploadDocument,
} from "@/lib/supabaseOps";
import { useSupabaseFileUrl } from "@/hooks/useSupabaseFileUrl";

function ConsentFormPreview({ className, payload }) {
  const payloadUrl = useSupabaseFileUrl(payload, "class-files");
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ color: "#0b1f7a" }}>Consent Form</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">{className}</p>
        {payloadUrl ? (
          <div className="space-y-3">
            <a
              href={payloadUrl}
              download={payload.fileName || "consent-form.pdf"}
              className="inline-flex rounded-full px-4 py-2 text-white font-semibold"
              style={{ backgroundColor: "#0b1f7a" }}
            >
              Download PDF
            </a>
            <iframe
              title="Consent Form"
              src={payloadUrl}
              className="w-full h-[70vh] rounded-xl border border-slate-200"
            />
          </div>
        ) : (
          <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "#EEF0FA" }}
            >
              <FileSignature size={28} style={{ color: "#0b1f7a" }} />
            </div>
            <p className="text-lg font-semibold text-slate-700">
              no forms for now
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StudentConsentForms({ user }) {
  const studentRecord = getStudentRecordForSession(user);
  const studentClassKey = classKey(user.standard, user.division);
  const consent = getConsentFormForClass(studentClassKey);
  const [uploadedForm, setUploadedForm] = useState(consent.teacherForm);
  const resolvedStudentId = studentRecord?.id || user.studentId || user.id || user.email || user.name;
  const mySubmission = consent.submissions?.[resolvedStudentId];
  const mySubmissionUrl = useSupabaseFileUrl(mySubmission, "class-files");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let active = true;

    const loadTeacherForm = async () => {
      if (!isSupabaseConfigured || !user?.standard || !user?.division) {
        if (active) {
          setUploadedForm(consent.teacherForm);
        }
        return;
      }

      try {
        const doc = await getLatestDocumentForClass(user.standard, user.division, "consent_form");
        if (!active) return;
        setUploadedForm(
          doc
            ? {
                fileName: doc.title || doc.file_name || doc.fileName || "consent-form.pdf",
                filePath: doc.file_path || doc.filePath || "",
                bucket: "class-files",
                uploadedAt: doc.created_at || doc.uploaded_at || "",
              }
            : consent.teacherForm,
        );
      } catch (error) {
        console.error(error);
        if (active) {
          setUploadedForm(consent.teacherForm);
        }
      }
    };

    loadTeacherForm();
    return () => {
      active = false;
    };
  }, [consent.teacherForm, user?.division, user?.standard]);

  const submit = async (file) => {
    if (!file) {
      toast.error("Please choose a filled consent PDF");
      return;
    }
    let linkedRecord = studentRecord;
    if (!linkedRecord?.id && user?.name && user?.standard && user?.division) {
      linkedRecord = saveStudentRecord({
        name: user.name,
        studentName: user.name,
        standard: user.standard,
        division: user.division,
        email: user.email || "",
      });
    }
    const submissionStudentId = linkedRecord?.id || resolvedStudentId;
    if (!submissionStudentId) {
      toast.error("Student record not found");
      return;
    }
    setUploading(true);
    try {
      let filePath = "";
      if (isSupabaseConfigured) {
        const { standard, division } = decodeClassKey(studentClassKey);
        try {
          const result = await uploadDocument(
            file,
            standard,
            division,
            "consent_form",
            `Consent Submission - ${linkedRecord?.name || user.name}`,
          );
          filePath = result?.path || "";
        } catch (error) {
          console.error(error);
        }
      }
      saveConsentSubmissionForClass(studentClassKey, submissionStudentId, {
        fileName: file.name,
        filePath: filePath || undefined,
        bucket: "class-files",
        submittedAt: new Date().toISOString(),
        studentName: linkedRecord?.name || user.name,
      });
      toast.success("Consent form submitted");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppShell>
      <div data-testid="consent-forms-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Consent Forms
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-6">
          <ConsentFormPreview
            className={classLabelFromKey(studentClassKey)}
            payload={uploadedForm}
          />

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Your Submission</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Uploaded class form:</p>
                <p className="font-semibold text-slate-800">
                  {uploadedForm?.fileName || "No consent form uploaded yet"}
                </p>
              </div>

              <div>
                <p className="text-sm text-slate-500">
                  Your submission status:
                </p>
                <p className="font-semibold text-slate-800">
                  {mySubmission ? "Submitted" : "Not submitted"}
                </p>
              </div>

              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => submit(e.target.files?.[0])}
                className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-[#0b1f7a] file:px-4 file:py-2 file:text-white hover:file:opacity-90"
              />

              <p className="text-xs text-slate-500">
                Choose your filled consent PDF to submit it for your class.
              </p>

              {uploading && <p className="text-sm text-slate-500">Uploading...</p>}

              {mySubmissionUrl && (
                <div className="rounded-xl border border-slate-200 p-4 space-y-2">
                  <p className="text-sm font-semibold text-slate-800">
                    Submitted file: {mySubmission.fileName}
                  </p>
                  <a
                    href={mySubmissionUrl}
                    download={mySubmission.fileName || "consent-form.pdf"}
                    className="inline-flex rounded-full px-4 py-2 text-white font-semibold"
                    style={{ backgroundColor: "#0b1f7a" }}
                  >
                    Open Submission
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function TeacherConsentForms({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [uploading, setUploading] = useState(false);
  const consent = teacherClassKey ? getConsentFormForClass(teacherClassKey) : { teacherForm: null, submissions: {} };
  const students = teacherClassKey ? getStudentsForClass(teacherClassKey) : [];
  const selectedStudent = students.find((student) => student.id === selectedStudentId) || students[0] || null;
  const selectedSubmission = selectedStudent ? consent.submissions?.[selectedStudent.id] : null;
  const selectedSubmissionUrl = useSupabaseFileUrl(selectedSubmission, "class-files");

  const uploadTeacherForm = async (file) => {
    if (!teacherClassKey) {
      toast.error("Please select a class first");
      return;
    }
    if (!file) {
      toast.error("Please choose a PDF");
      return;
    }
    setUploading(true);
    try {
      const { standard, division } = decodeClassKey(teacherClassKey);
      if (isSupabaseConfigured) {
        try {
          const result = await uploadDocument(file, standard, division, "consent_form", "Consent Form");
          saveConsentFormForClass(teacherClassKey, {
            fileName: file.name,
            filePath: result?.path || undefined,
            bucket: "class-files",
            uploadedAt: new Date().toISOString(),
          });
          toast.success("Consent form uploaded");
          return;
        } catch (error) {
          console.error(error);
        }
      }
      saveConsentFormForClass(teacherClassKey, {
        fileName: file.name,
        filePath: undefined,
        bucket: "class-files",
        uploadedAt: new Date().toISOString(),
      });
      toast.success("Consent form uploaded");
    } finally {
      setUploading(false);
    }
  };

  const statusTone = selectedSubmission ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600";

  return (
    <AppShell>
      <div data-testid="consent-forms-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Consent Forms
        </h2>

        <TeacherClassPicker teacher={user} onChange={setTeacherClassKey} />

        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Upload Class PDF</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => uploadTeacherForm(e.target.files?.[0])}
                className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-[#0b1f7a] file:px-4 file:py-2 file:text-white hover:file:opacity-90"
              />
              {uploading && <p className="text-sm text-slate-500">Uploading...</p>}
              {consent.teacherForm?.fileName && (
                <p className="text-sm text-slate-600">
                  Current form: <span className="font-semibold">{consent.teacherForm.fileName}</span>
                </p>
              )}
            </CardContent>
          </Card>

          <ConsentFormPreview
            className={teacherClassKey ? classLabelFromKey(teacherClassKey) : "Select a class first"}
            payload={consent.teacherForm}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>Student Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            {!teacherClassKey ? (
              <p className="text-sm text-slate-500">Select a class to view the student list.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-6">
                <div className="space-y-2">
                  {students.map((student) => {
                    const submitted = Boolean(consent.submissions?.[student.id]);
                    return (
                      <button
                        key={student.id}
                        type="button"
                        onClick={() => setSelectedStudentId(student.id)}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                          selectedStudent?.id === student.id
                            ? "border-[#0b1f7a] bg-[#EEF0FA]"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-800">{student.name}</p>
                            <p className="text-xs text-slate-500">
                              Roll {student.rollNo} · {student.standard} {student.division}
                            </p>
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${submitted ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                            {submitted ? "Submitted" : "Pending"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                  {selectedStudent ? (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-800">{selectedStudent.name}</p>
                          <p className="text-xs text-slate-500">
                            {selectedStudent.standard} {selectedStudent.division}
                          </p>
                        </div>
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusTone}`}>
                          {selectedSubmission ? "Submitted" : "Pending"}
                        </span>
                      </div>
                      {selectedSubmissionUrl ? (
                        <>
                          <p className="text-sm text-slate-600">
                            Submitted on {new Date(selectedSubmission.submittedAt).toLocaleString()}
                          </p>
                          <a
                            href={selectedSubmissionUrl}
                            download={selectedSubmission.fileName || "consent-form.pdf"}
                            className="inline-flex rounded-full px-4 py-2 text-white font-semibold"
                            style={{ backgroundColor: "#0b1f7a" }}
                          >
                            Open Submitted PDF
                          </a>
                        </>
                      ) : (
                        <p className="text-sm text-slate-500">This student has not submitted the form yet.</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">
                      Tap a student profile to see if they submitted the consent form.
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

export default function ConsentForms() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherConsentForms user={user} />;
  }
  return <StudentConsentForms user={user || {}} />;
}
